import type {CryptoPayinSession, CryptoSetupSession} from 'src/types';
import type {
  FeatureFlags,
  SupportedCurrency,
  WalletSession,
  PaymentDetails,
  TransactionDetails,
  ExternalPaymentDetails,
  ConsumerWallet,
  MerchantBranding,
  MandateDetails,
} from 'src/v2/state/types';
import {COLORS} from 'src/v2/util/color';

type GraphQLFeatureFlags =
  | CryptoPayinSession['feature_flags']
  | CryptoSetupSession['feature_flags'];
type GraphQLSupportedCurrency = CryptoPayinSession['supported_currencies'][0];
type GraphQLWalletSession = CryptoPayinSession['wallet_sessions'][0];
type GraphQLPaymentDetails = CryptoPayinSession['payment_details'];
type GraphQLTransactionDetails = CryptoPayinSession['transaction_details'];
type GraphQLExternalPaymentDetails =
  CryptoPayinSession['external_payment_details'];
type GraphQLConsumerWallet = CryptoPayinSession['active_wallet'];
type GraphQLMandateDetails = CryptoSetupSession['payment_mandate'];
/**
 * Maps GraphQL feature flags to store feature flags format
 */
export const convertGraphQlToFeatureFlags = (
  flags: GraphQLFeatureFlags,
): FeatureFlags => ({
  useAsyncConfirmationFlow: flags.use_async_confirmation_flow,
  enable_crypto_payins_pay_from_exchange:
    flags.enable_crypto_payins_pay_from_exchange,
  enable_crypto_payins_direct_deposit:
    flags.enable_crypto_payins_direct_deposit,
  enable_underpayment_detection:
    'enable_underpayment_detection' in flags
      ? flags.enable_underpayment_detection
      : false,
  enable_gasless_v2: flags.enable_gasless_v2,
  enable_smart_contract_wallet_detection:
    flags.enable_smart_contract_wallet_detection,
  crypto_payins_generic_payment_title:
    flags.crypto_payins_generic_payment_title,
  enable_pay_with_any_crypto:
    'enable_pay_with_any_crypto' in flags
      ? flags.enable_pay_with_any_crypto
      : false,
});

/**
 * Maps GraphQL supported currency to store supported currency format
 */
export const convertGraphQlToSupportedCurrency = (
  currency: GraphQLSupportedCurrency,
): SupportedCurrency => ({
  id: currency.id,
  assetDisplayName: currency.asset_display_name,
  assetCode: currency.asset_code,
  currencyMinorUnits: currency.currency_minor_units,
  currencySymbol: currency.currency_symbol,
  currencyNetwork: currency.currency_network,
  mainnet: currency.mainnet,
  contractAddress: currency.contract_address,
  chainId: currency.chain_id,
});

/**
 * Maps GraphQL wallet session to store wallet session format
 */
export const convertGraphQlToWalletSession = (
  ws: GraphQLWalletSession,
): WalletSession => ({
  id: ws.id,
  livemode: ws.livemode,
  merchant: ws.merchant,
  address: ws.address,
  connected: ws.connected,
  network: ws.network,
  provider: ws.provider,
  type: ws.type,
  verificationState: ws.verification_state,
});

/**
 * Maps GraphQL payment details to store payment details format
 */
export const convertGraphQlToPaymentDetails = (
  details: GraphQLPaymentDetails,
): PaymentDetails => ({
  amount: details.amount,
  currency: details.currency,
  payauthzId: details.payauthz_id,
  returnUrl: details.return_url,
  originalAmount: details.original_amount,
  originalCurrency: details.original_currency,
});

/**
 * Maps GraphQL transaction details to store transaction details format
 */
export const convertGraphQlToTransactionDetails = (
  details: GraphQLTransactionDetails | null,
): TransactionDetails => {
  if (!details) {
    return {
      walletAddress: null,
      blockchainTxId: null,
      destinationAddress: null,
      destinationCurrency: null,
      destinationNetwork: null,
      destinationAmount: null,
      solanaLatestBlockhash: null,
      solanaDestinationTokenAccountAddress: null,
    };
  }

  return {
    walletAddress: details.wallet_address,
    blockchainTxId: details.blockchain_tx_id,
    destinationAddress: details.destination_address,
    destinationCurrency: details.destination_currency,
    destinationNetwork: details.destination_network,
    destinationAmount: details.destination_amount,
    solanaLatestBlockhash: details.solana_latest_blockhash,
    solanaDestinationTokenAccountAddress:
      details.solana_destination_token_account_address,
  };
};

/**
 * Maps GraphQL external payment details to store external payment details format
 */
export const convertGraphQlToExternalPaymentDetails = (
  details: GraphQLExternalPaymentDetails | null,
): ExternalPaymentDetails | null => {
  if (!details) {
    return null;
  }

  return {
    paymentId: details.payment_id,
    paymentUrl: details.payment_url,
    paymentQrCode: details.payment_qr_code,
    externalPaymentPartner: details.external_payment_partner,
  };
};

/**
 * Maps GraphQL mandate details to store mandate details format
 */
export const convertGraphQlToMandateDetails = (
  mandate: GraphQLMandateDetails,
): MandateDetails => ({
  fpmandateId: mandate.fpmandate_id,
  returnUrl: mandate.return_url,
});

/**
 * Maps GraphQL consumer wallet to store consumer wallet format
 */
export const convertGraphQlToConsumerWallet = (
  wallet: GraphQLConsumerWallet | null,
): ConsumerWallet | null => {
  if (!wallet) return null;

  return {
    id: wallet.id,
    livemode: wallet.livemode,
    merchant: wallet.merchant,
    address: wallet.address,
    connected: wallet.connected,
    network: wallet.network,
    verificationState: wallet.verification_state,
  };
};

export const convertGraphQlToMerchantBranding = (
  backgroundColor: string | null,
  buttonColor: string | null,
  businessName: string | null,
  logoUrl: string | null,
  consumerAccount: string | null,
): MerchantBranding => {
  const defaultButtonColor = consumerAccount
    ? COLORS.defaultLinkButtonColor
    : COLORS.defaultButtonColor;

  return {
    backgroundColor: backgroundColor || COLORS.defaultBackgroundColor,
    buttonColor: buttonColor || defaultButtonColor,
    businessName,
    logoUrl,
  };
};
